import { CommonRegistry } from '../components/components';
import { World } from '../World';

export interface EntityBounds {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/**
 * Finds the closest interactable entity to the player within a given radius.
 * This logic mirrors both the client hint rendering and server interaction intent logic.
 */
export function findClosestInteractable<Registry extends CommonRegistry>(
  world: World<Registry>,
  playerEntityId: number,
  radius: number,
  componentClasses: (keyof Registry)[] = ['Interactable', 'Transform'] as (keyof Registry)[],
  excludedComponentClasses: (keyof Registry)[] = [],
): number | null {
  const playerTransform = world.getComponent(playerEntityId, 'Transform');
  const playerCollider = world.getComponent(playerEntityId, 'Collider');

  if (!playerTransform || !playerCollider) {
    return null;
  }

  // Calculate player bounds
  const playerBounds: EntityBounds = {
    left: playerTransform.x + playerCollider.offsetX,
    top: playerTransform.y + playerCollider.offsetY,
    right: playerTransform.x + playerCollider.offsetX + playerCollider.width,
    bottom: playerTransform.y + playerCollider.offsetY + playerCollider.height,
  };

  let minDistance = Infinity;
  let nearestId: number | null = null;

  // Scan candidates
  const entities = world.query(componentClasses, excludedComponentClasses);

  for (const entityId of entities) {
    if (entityId === playerEntityId) continue;

    const transform = world.getComponent(entityId, 'Transform');
    if (!transform) continue;

    const collider = world.getComponent(entityId, 'Collider');

    // Determine entity bounds (Collider -> Point)
    let entityBounds: EntityBounds;
    if (collider) {
      entityBounds = {
        left: transform.x + collider.offsetX,
        top: transform.y + collider.offsetY,
        right: transform.x + collider.offsetX + collider.width,
        bottom: transform.y + collider.offsetY + collider.height,
      };
    } else {
      entityBounds = {
        left: transform.x,
        top: transform.y,
        right: transform.x,
        bottom: transform.y,
      };
    }

    // Calculate distance between rectangles (0 if overlapping)
    const distX = Math.max(
      entityBounds.left - playerBounds.right,
      playerBounds.left - entityBounds.right,
      0,
    );
    const distY = Math.max(
      entityBounds.top - playerBounds.bottom,
      playerBounds.top - entityBounds.bottom,
      0,
    );
    const dist = Math.sqrt(distX * distX + distY * distY);

    if (dist <= radius && dist < minDistance) {
      minDistance = dist;
      nearestId = entityId;
    }
  }

  return nearestId;
}
